import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';

export interface SkillsAndExperienceData {
  readonly id: number;
  readonly isStudent?: string;
  readonly university?: string;
  readonly faculty?: string;
  readonly speciality?: string;
  readonly courseYear?: string;
  readonly candidateId: number;
  readonly candidate?: Candidate;
}

export interface SkillsAndExperience extends Readonly<SkillsAndExperienceData> {}
export class SkillsAndExperience extends BusinessObjectBase {
  static readonly entityName = 'SkillsAndExperience' as const;
  static readonly plural = 'skills-and-experiences';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<SkillsAndExperience[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new SkillsAndExperience(data));
  }

  static async findOne(filter?: any): Promise<SkillsAndExperience | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new SkillsAndExperience(data) : null;
  }

  static async findById(
    id: number,
    filter?: any,
  ): Promise<SkillsAndExperience> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new SkillsAndExperience(data);
  }

  static async create(
    data: Omit<SkillsAndExperienceData, 'id'>,
  ): Promise<SkillsAndExperience> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new SkillsAndExperience(result);
  }

  static async updateById(
    id: number,
    data: Partial<SkillsAndExperienceData>,
  ): Promise<SkillsAndExperience> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new SkillsAndExperience(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
